import React, { useState, useEffect } from 'react';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';

const STORAGE_KEY = 'kynaix_cookie_consent';

const DEFAULT_PREFS = {
  necessary: true,   // always true, cannot be toggled
  preferences: false,
  analytics: false,
  marketing: false,
};

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveConsent(prefs) {
  try {
    const payload = { ...prefs, decided: true };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    // Notify the rest of the app so GA consent mode can be applied immediately
    window.dispatchEvent(new CustomEvent('kynaix:consent-updated', { detail: payload }));
  } catch {
    // ignore storage errors
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const saved = loadConsent();
    if (!saved || !saved.decided) {
      // Slight delay so the page renders before the banner pops in
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAllowAll = () => {
    const all = { necessary: true, preferences: true, analytics: true, marketing: true };
    saveConsent(all);
    setVisible(false);
  };

  const handleNecessaryOnly = () => {
    const minimal = { necessary: true, preferences: false, analytics: false, marketing: false };
    saveConsent(minimal);
    setVisible(false);
  };

  const handleSaveCustom = () => {
    saveConsent(prefs);
    setVisible(false);
  };

  const toggle = (key) => {
    if (key === 'necessary') return;
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-3 md:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Cookie consent"
    >
      <div className="max-w-3xl mx-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Blue accent bar */}
        <div className="h-1 bg-blue-600 w-full" />

        <div className="p-5">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5">
              <Cookie size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="font-semibold text-gray-900 dark:text-white text-sm leading-snug">
                This website uses cookies
              </p>
            </div>
            <button
              onClick={handleNecessaryOnly}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0 mt-0.5"
              aria-label="Dismiss — use necessary cookies only"
            >
              <X size={18} />
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
            We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. You can choose which cookies to accept. Read our{' '}
            <a href="/privacy" className="text-blue-600 underline hover:text-blue-700">Privacy Policy</a>{' '}
            and{' '}
            <a href="/cookies" className="text-blue-600 underline hover:text-blue-700">Cookie Policy</a>{' '}
            for more details.
          </p>

          {/* Customize accordion */}
          {showCustomize && (
            <div className="mb-4 space-y-2 border border-gray-100 dark:border-gray-700 rounded-xl p-3 bg-gray-50 dark:bg-gray-800">
              {[
                { key: 'necessary', label: 'Necessary', desc: 'Required for the website to function. Cannot be disabled.' },
                { key: 'preferences', label: 'Preferences', desc: 'Remember your settings and theme choices.' },
                { key: 'analytics', label: 'Analytics', desc: 'Help us understand how you use the platform.' },
                { key: 'marketing', label: 'Marketing', desc: 'Deliver relevant ads and track campaign performance.' },
              ].map(({ key, label, desc }) => (
                <label
                  key={key}
                  className={`flex items-start justify-between gap-3 cursor-pointer rounded-lg px-2 py-1.5 transition-colors ${key !== 'necessary' ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100 leading-tight">{label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{desc}</p>
                  </div>
                  <div className="flex-shrink-0 mt-0.5">
                    {key === 'necessary' ? (
                      <span className="text-xs text-green-600 font-medium">Always on</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggle(key)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${prefs[key] ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                        aria-checked={prefs[key]}
                        role="switch"
                        aria-label={label}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${prefs[key] ? 'translate-x-4' : 'translate-x-0.5'}`}
                        />
                      </button>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              onClick={handleAllowAll}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
            >
              Allow all cookies
            </button>

            <button
              onClick={() => setShowCustomize((v) => !v)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
            >
              {showCustomize ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              Customize
            </button>

            {showCustomize ? (
              <button
                onClick={handleSaveCustom}
                className="flex-1 sm:flex-none border border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
              >
                Save preferences
              </button>
            ) : (
              <button
                onClick={handleNecessaryOnly}
                className="flex-1 sm:flex-none text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm px-4 py-2.5 rounded-xl transition-colors"
              >
                Use necessary cookies only
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Hook to read saved cookie consent preferences anywhere in the app */
export function useCookieConsent() {
  const saved = loadConsent();
  if (!saved || !saved.decided) return DEFAULT_PREFS;
  return saved;
}
